/**
 * TemaLegadoProvider — dá um tema às telas que ainda usam `@mui/styles`.
 *
 * ## O problema que resolve
 *
 * 14 telas montam estilos com `makeStyles((theme) => ({ … theme.spacing(2) … }))`
 * do pacote legado `@mui/styles`. Esse `makeStyles` **exige** um tema no
 * contexto: sem provider ele recebe um objeto vazio e a tela morre no primeiro
 * `theme.spacing` — `TypeError: theme.spacing is not a function`. Era o que
 * acontecia com o dashboard do evento, os painéis de quadra e as premiações.
 *
 * ## Por que o import é de `@mui/styles`, e não de `@mui/material/styles`
 *
 * **Trocar este import quebra tudo de novo, em silêncio.** As duas famílias
 * escrevem em contextos React diferentes nesta instalação:
 *
 * | Consumidor | Lê o contexto de |
 * |---|---|
 * | `makeStyles` do `@mui/styles` 6.5 | `@mui/private-theming` **6.4.9** (raiz) |
 * | `ThemeProvider` do `@mui/material/styles` 7.3 | `@mui/private-theming` **7.3.7** (aninhado em `@mui/system`) |
 *
 * O provider "moderno" alimentaria o contexto v7, que o `makeStyles` v6 não lê —
 * as telas continuariam quebrando, e o teste continuaria verde se ele também
 * usasse o provider errado. Por isso o `ThemeProvider` vem de `@mui/styles`.
 *
 * ## Por que `createTheme()` sem argumento
 *
 * É o tema default do MUI — exatamente o que os componentes já usam
 * internamente quando não há provider. Assim o único efeito de montar isto é
 * destravar as telas legadas; nada mais no app muda de aparência.
 *
 * **Não customize cores, espaçamento ou tipografia aqui.** No dia em que isso
 * acontecer, deixa de ser conserto pontual e vira mudança visual global.
 *
 * ## Isto é dívida técnica, não arquitetura
 *
 * `@mui/styles` está deprecado no MUI v5+ e não tem futuro com React 19. Este
 * provider é o conserto rápido. A saída é migrar as 14 telas para `sx`, uma a
 * uma — ver `docs/DEBITO-TECNICO-MUI-STYLES.md`. Quando a última sair, este
 * arquivo e a dependência `@mui/styles` vão junto.
 */

import React from 'react';
import { ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material/styles';

/** Tema default do MUI, criado uma vez. */
const temaLegado = createTheme();

export default function TemaLegadoProvider({ children }) {
  return <ThemeProvider theme={temaLegado}>{children}</ThemeProvider>;
}

export { temaLegado };
